/**
 * Контур черновика стены — такой, каким стена встанет после клика.
 *
 * Черновик — не голая полоса от точки до точки: начало или конец в углу стыкуются с соседней
 * стеной так же, как на чертеже (митра, внешний угол закрыт). Иначе у угла полоса торчала бы
 * за соседнюю стену на полтолщины и не доходила до её внешнего угла, а после клика угол
 * выглядел бы уже по-другому. Соседние стены в этом углу тоже меняют контур — их новый
 * контур отдаётся вместе с черновиком, холст рисует стены по нему.
 * Дотягивание свободного конца (`stretch`) двигает его узел, как и при постройке.
 * Только чистые функции.
 */
import { dist, type Pt } from "@/shared/lib/geometry";
import type { SnapResult, WallGraph } from "@/shared/plan";
import { buildWallOutlines, type WallOutline } from "@/widgets/plan";

/** Ближе этого, мм, к узлу — точка и есть узел. */
const NODE_SLACK = 1;

const DRAFT_WALL = "__draft";
const DRAFT_FROM = "__draft_from";
const DRAFT_TO = "__draft_to";

export interface DraftOutlines {
	/** Контур самой будущей стены */
	draft: WallOutline;
	/** Новые контуры соседних стен, с которыми черновик сходится в узле */
	neighbors: Record<string, WallOutline>;
}

const nodeAt = (graph: WallGraph, point: Pt) =>
	Object.values(graph.nodes).find((n) => dist(n, point) <= NODE_SLACK)?.id ?? null;

export const wallDraftOutlines = (
	graph: WallGraph,
	from: Pt,
	to: Pt,
	thickness: number,
	stretch?: SnapResult["stretch"],
): DraftOutlines | null => {
	if (dist(from, to) < NODE_SLACK) return null;
	const nodes = { ...graph.nodes };
	if (stretch && nodes[stretch.nodeId]) {
		nodes[stretch.nodeId] = { ...nodes[stretch.nodeId], x: to.x, y: to.y };
	}
	const plan = { ...graph, nodes };
	const a = nodeAt(plan, from) ?? DRAFT_FROM;
	const b = (stretch && nodes[stretch.nodeId] ? stretch.nodeId : nodeAt(plan, to)) ?? DRAFT_TO;
	if (a === b) return null;
	if (a === DRAFT_FROM) nodes[a] = { id: a, x: from.x, y: from.y };
	if (b === DRAFT_TO) nodes[b] = { id: b, x: to.x, y: to.y };
	const outlines = buildWallOutlines({
		...plan,
		walls: { ...graph.walls, [DRAFT_WALL]: { id: DRAFT_WALL, a, b, thickness, height: 0 } },
	});
	const draft = outlines[DRAFT_WALL];
	if (!draft) return null;
	const neighbors: Record<string, WallOutline> = {};
	for (const wall of Object.values(graph.walls)) {
		const touches = [a, b].some((id) => id === wall.a || id === wall.b);
		if (touches && outlines[wall.id]) neighbors[wall.id] = outlines[wall.id];
	}
	return { draft, neighbors };
};
